<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AdminApiError } from '../api/adminClient'
import { adminAuthApi } from '../api/adminAuth'
import { adminSessionApi, applyAdminSession } from '../api/adminSession'
import { markAdminAuthenticated, safeReturnPath } from '../router'

const route = useRoute()
const router = useRouter()
const username = ref('')
const password = ref('')
const errorMessage = ref('')
const submitting = ref(false)
let destroyed = false

function returnPath() {
  return safeReturnPath(route.query.return_to)
}

function describeError(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.status === 400) return '登录信息格式不正确，请检查后重试。'
    if (error.status === 403) return '账号没有可用的后台权限。'
    if (error.status === 429) return '登录尝试过于频繁，请稍后再试。'
    if (error.status === 503) return '登录服务暂不可用，请稍后再试。'
  }
  return '账号或密码错误。'
}

async function submit() {
  if (submitting.value) return
  errorMessage.value = ''
  if (!username.value.trim() || !password.value) {
    errorMessage.value = '请输入账号和密码。'
    return
  }
  submitting.value = true
  try {
    await adminAuthApi.login({ username: username.value, password: password.value })
    const session = await adminSessionApi.getSession()
    if (destroyed) return
    applyAdminSession(session)
    markAdminAuthenticated()
    await router.replace(returnPath())
  } catch (error) {
    if (!destroyed) errorMessage.value = describeError(error)
  } finally {
    password.value = ''
    submitting.value = false
  }
}

onBeforeUnmount(() => {
  destroyed = true
  password.value = ''
})
</script>

<template>
  <article id="main-content" class="login-page" aria-labelledby="login-title" tabindex="-1">
    <p class="screen-kicker">BondPet 运营台</p>
    <h1 id="login-title">管理员登录</h1>
    <p class="login-intro">使用后台账号登录运营管理工作台。</p>
    <el-alert v-if="errorMessage" type="error" :closable="false" :title="errorMessage" role="alert" />
    <form class="login-form" novalidate @submit.prevent="submit">
      <el-form-item label="账号" required>
        <el-input v-model="username" name="username" autocomplete="username" autofocus maxlength="128" placeholder="请输入账号" />
      </el-form-item>
      <el-form-item label="密码" required>
        <el-input v-model="password" name="password" type="password" autocomplete="current-password" show-password maxlength="256" placeholder="请输入密码" />
      </el-form-item>
      <el-button class="login-submit" type="primary" native-type="submit" :loading="submitting">登录</el-button>
    </form>
  </article>
</template>
